import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'

import { StatusTag } from '../../../../../../components/tableTemplates'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config/context'
import {
  useInvoiceById,
  useInvoiceItemAllowanceChargeById,
} from '../../../../data'

type Props = {
  infoMode: boolean
  setInfoMode: (params: boolean) => void
  editMode: boolean
  setEditMode: (params: boolean) => void
  setDisplayEditItem: (params: boolean) => void
  setDisplayInfoItem: (params: boolean) => void
  visible: boolean
  setVisible: (params: boolean) => void
  invoiceId: string
  itemId: string
  id: string
  setIdInfoItemAllowanceCharge: (params: string) => void
  setInfoActiveTabViewPanelIndex: (params: number) => void
  setEditActiveTabViewPanelIndex: (params: number) => void
}

export const InfoInvoiceItemAllowanceCharge: React.FC<Props> = ({
  infoMode,
  setInfoMode,
  editMode,
  setEditMode,
  setDisplayEditItem,
  setDisplayInfoItem,
  visible,
  setVisible,
  invoiceId,
  itemId,
  id,
  setIdInfoItemAllowanceCharge,
  setInfoActiveTabViewPanelIndex,
  setEditActiveTabViewPanelIndex,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  const { data: invoice } = useInvoiceById(invoiceId)
  const { data: invoiceItemAllowanceCharge, isLoading } =
    useInvoiceItemAllowanceChargeById(invoiceId, itemId, id)

  const currency = invoice?.Payload.OrderCurrency

  // handleClose
  const handleClose = () => {
    setVisible(false)
    setIdInfoItemAllowanceCharge('')
    setInfoMode(false)
    setEditMode(false)

    if (infoMode === true) {
      setDisplayInfoItem(true)
      setInfoActiveTabViewPanelIndex(1)
    } else if (editMode === true) {
      setDisplayEditItem(true)
      setEditActiveTabViewPanelIndex(1)
    }
  }

  // ************************************************************
  // components
  // ************************************************************
  // alcType
  const alcTypeLabel = <label htmlFor="alcType" className="text-sm"></label>

  const alcTypeComponent = (
    <InputText
      id="alcType"
      className="w-full"
      value={
        invoiceItemAllowanceCharge?.AlcType === 'Surcharge'
          ? t('common.codes.alcType.surcharge')
          : t('common.codes.alcType.discount')
      }
      disabled
    />
  )

  const alcTypeWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItemAllowanceCharge}
      component={alcTypeComponent}
      label={alcTypeLabel}
      isLoading={isLoading}
    />
  )

  // descriptionText
  const descriptionTextLabel = (
    <label htmlFor="descriptionText" className="text-sm"></label>
  )

  const descriptionTextComponent = (
    <InputText
      id="descriptionText"
      className="w-full"
      value={invoiceItemAllowanceCharge?.Description?.Text}
      disabled
    />
  )

  const descriptionTextWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItemAllowanceCharge}
      component={descriptionTextComponent}
      label={descriptionTextLabel}
      isLoading={isLoading}
    />
  )

  // percentage
  const percentageLabel = (
    <label htmlFor="percentage" className="text-sm"></label>
  )

  const percentageComponent = (
    <InputNumber
      id="percentage"
      value={invoiceItemAllowanceCharge?.Percentage}
      className="w-full"
      suffix=" %"
      minFractionDigits={2}
      locale={language}
      disabled
    />
  )

  const percentageWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItemAllowanceCharge}
      component={percentageComponent}
      label={percentageLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPercentage
  const confirmationPercentageLabel = (
    <label htmlFor="confirmationPercentage" className="text-sm"></label>
  )

  const confirmationPercentageComponent = (
    <InputNumber
      id="confirmationPercentage"
      value={invoiceItemAllowanceCharge?.ConfirmationPercentage}
      className="w-full"
      min={0}
      suffix=" %"
      minFractionDigits={2}
      showButtons
      locale={language}
      disabled
    />
  )

  const confirmationPercentageWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItemAllowanceCharge}
      component={confirmationPercentageComponent}
      label={confirmationPercentageLabel}
      isLoading={isLoading}
    />
  )

  // amount
  const amountLabel = <label htmlFor="amount" className="text-sm"></label>

  const amountComponent = (
    <InputNumber
      id="amount"
      mode="currency"
      currency={currency}
      value={invoiceItemAllowanceCharge?.Amount}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const amountWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItemAllowanceCharge}
      component={amountComponent}
      label={amountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAmount
  const confirmationAmountLabel = (
    <label htmlFor="confirmationAmount" className="text-sm"></label>
  )

  const confirmationAmountComponent = (
    <InputNumber
      id="confirmationAmount"
      mode="currency"
      showButtons
      currency={currency}
      value={invoiceItemAllowanceCharge?.ConfirmationAmount}
      className="w-full"
      min={0}
      locale={language}
      disabled
    />
  )

  const confirmationAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.infoInvoiceItemAllowanceCharge}
      component={confirmationAmountComponent}
      label={confirmationAmountLabel}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <div className="flex align-content-center flex-wrap">
      <span className="mr-3">
        {t(
          'invoice.invoiceItems.table.info.dialog.allowanceCharges.table.info.dialog.title',
        )}
      </span>

      <StatusTag status={invoiceItemAllowanceCharge?.Status} />
    </div>
  )

  // footer
  const footer = (
    <Button
      label={t('common.buttons.back')}
      icon="pi pi-check"
      onClick={handleClose}
      autoFocus
      className="mt-3 w-full md:w-auto"
    />
  )

  return (
    <>
      <Dialog
        header={header}
        visible={visible}
        style={{ width: '40rem' }}
        breakpoints={{ [calculateBreakpointWidth(40)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        {/* alcType */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{alcTypeWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* descriptionText */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{descriptionTextWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* percentage */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{percentageWrapper}</div>
          <div className="col-12 md:col-6">{confirmationPercentageWrapper}</div>
        </div>

        <Divider className="md:hidden" />

        {/* amount */}
        <div className="formgrid grid">
          <div className="col-12 md:col-6">{amountWrapper}</div>
          <div className="col-12 md:col-6">{confirmationAmountWrapper}</div>
        </div>
      </Dialog>
    </>
  )
}
